import { george, helpers, pr, theme } from "aaa";
import { ErrorBox } from "components/common/ErrorBox";
import { GdsFakeActionButton } from "components/common/GdsFakeActionButton";
import { GdsFakeActionButtonContainer } from "components/common/GdsFakeActionButtonContainer";
import { ui } from "components/util/uiHelpers";
import * as FileSaver from "file-saver";
import { observer } from "mobx-react";
import { GenericAPIClient } from "network/APIClient";
import * as React from "react";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import { pushRoute } from "stores/RouterFunctions";
import { b64ToBlob, b64ToUint8 } from "util/helpers";
import { IchAppRoutes } from "./IchAppRoutes";
import { IchAppStore } from "./IchAppStore";

type IProps = {
    store: IchAppStore
};

type AgreementDocument = {
    agreement: {
        contentBase64: string;
        contentType: string;
        name: string;
    }
};

export const IchAppContract = observer(({ store }: IProps) => {
    const [showDownloadError, setShowDownloadError] = React.useState(false);
    const [showReadContractError, setShowReadContractError] = React.useState(false);
    const [showSignError, setShowSignError] = React.useState(false);
    const [waitForAuthorization, setWaitForAuthorization] = React.useState(false);

    const buttonDisabled = waitForAuthorization || !store.downloadContractChecked || !store.downloadedContract || !store.readContractChecked || !store.signChecked;

    const handleAuthorization = async () => {
        setWaitForAuthorization(true);
        let ok = true;
        if (!store.downloadContractChecked || !store.downloadedContract) {
            setShowDownloadError(true);
            ok = false;
        }
        if (!store.readContractChecked) {
            setShowReadContractError(true);
            ok = false;
        }
        if (!store.signChecked) {
            setShowSignError(true);
            ok = false;
        }

        if (!ok) {
            return;
        }

        try {
            await store.postAuthorization();
            pushRoute(IchAppRoutes.SIGNING);
        } catch (error) {
            pushRoute("/error");
        } finally {
            setWaitForAuthorization(false);
        }
    };

    const handleDownload = async () => {
        try {
            const agreements = await store.postAgreements();
            const url = agreements.link.href;

            const response = await GenericAPIClient.requestType<AgreementDocument>({
                // For local testing we replace the https:// prefix with our proxy url
                url: url.replace("https://", ""),
                method: "GET",
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/x-www-form-urlencoded"
                },
                parse: json => json as AgreementDocument
            });

            if (atob(response.agreement.contentBase64).slice(0, 1025).includes("%PDF-")) {
                if (helpers.isAndroid() || helpers.isIos()) {
                    store.pdfData = b64ToUint8(response.agreement.contentBase64);
                    pushRoute(IchAppRoutes.PDF);
                } else {
                    const blob = b64ToBlob(response.agreement.contentBase64, response.agreement.contentType);
                    FileSaver.saveAs(blob, response.agreement.name);
                }
            }

            setShowDownloadError(false);
            store.downloadedContract = true;
        } catch (error) {
            pushRoute("/error");
        }
    };

    return (
        <george.Card
            elevated={false}
            style={{ borderRadius: 8 }}
        >
            <div style={{ lineHeight: "24px" }}>
                <div style={{ padding: "32px 16px" }}>
                    <pr.P_22_TITLE style={{ fontWeight: 700, lineHeight: "28px", marginTop: 16 }}>
                        {ui.__("ichApp.contract.title")}
                    </pr.P_22_TITLE>
                    <div style={{ marginTop: 32 }} >
                        <george.ApprovalEmbedded
                            defaultChecked={store.downloadContractChecked}
                            hideTitle
                            onCheck={(_, checked) => {
                                store.downloadContractChecked = checked;
                                if (checked) {
                                    setShowDownloadError(false);
                                }
                            }}
                            message={ui.__("ichApp.contract.download.text")}
                        />
                        {showDownloadError &&
                            <ErrorBox
                                content={(
                                    <pr.P_16_CONTENT>{ui.__("ichApp.contract.download.error")}</pr.P_16_CONTENT>
                                )}
                            />
                        }
                        <div
                            role="button"
                            onClick={handleDownload}
                            className="theme-secondary-action-label-color"
                            style={{ display: "flex", alignItems: "center", fontWeight: 600, fontSize: 14, cursor: "pointer", marginTop: theme.MARGIN, marginLeft: 35 }}
                        >
                            <pr.IconDownload style={{ marginRight: 5 }} />
                            <span>{ui.__("common.downloadDocument")}</span>
                        </div>
                    </div>
                    <div style={{ marginTop: 32 }} >
                        <george.ApprovalEmbedded
                            defaultChecked={store.readContractChecked}
                            hideTitle
                            onCheck={(_, checked) => {
                                store.readContractChecked = checked;
                                if (checked) {
                                    setShowReadContractError(false);
                                }
                            }}
                            message={ui.__("ichApp.contract.readContract.text")}
                        />
                        {showReadContractError &&
                            <ErrorBox
                                content={(
                                    <pr.P_16_CONTENT>{ui.__("ichApp.contract.readContract.error")}</pr.P_16_CONTENT>
                                )}
                            />
                        }
                    </div>
                    <div style={{ marginTop: 32 }}>
                        <george.ApprovalEmbedded
                            defaultChecked={store.signChecked}
                            hideTitle
                            onCheck={(_, checked) => {
                                store.signChecked = checked;
                                if (checked) {
                                    setShowSignError(false);
                                }
                            }}
                            message={ui.__("ichApp.contract.sign.text")}
                        />
                        {showSignError &&
                            <ErrorBox
                                content={(
                                    <pr.P_16_CONTENT>{ui.__("ichApp.contract.sign.error")}</pr.P_16_CONTENT>
                                )}
                            />
                        }
                    </div>
                </div>
                <pr.LightSeparatorLine />
                <GdsFakeActionButtonContainer>
                    <GdsFakeActionButton color="primary" onClick={handleAuthorization} fullWidth={externalInterfaceStore.isGeorgeApp} isLoading={waitForAuthorization}
                        disabled={buttonDisabled}>
                        {ui.__("button.continue")}

                    </GdsFakeActionButton>
                    <GdsFakeActionButton
                        onClick={() => {
                            store.resetAgreements();
                            pushRoute(IchAppRoutes.REGISTRATION);
                        }}
                        fullWidth={externalInterfaceStore.isGeorgeApp}
                    >
                        {ui.__("common.back")}
                    </GdsFakeActionButton>
                </GdsFakeActionButtonContainer>
            </div>
        </george.Card >
    );
});



// WEBPACK FOOTER //
// ./src/components/ichApp/IchAppContract.tsx